import React, { useMemo } from 'react';
import styled from 'styled-components';

// Converte URLs de vídeo em iframes
function convertOembedToIframe(html) {
    if (!html) return '';

    // Converte <oembed> para iframe
    let processedHtml = html.replace(
        /<oembed[^>]*url="([^"]*)"[^>]*><\/oembed>/gi,
        (match, url) => createVideoEmbed(url)
    );

    // Também trata o formato com atributo src
    processedHtml = processedHtml.replace(
        /<oembed[^>]*src="([^"]*)"[^>]*><\/oembed>/gi,
        (match, url) => createVideoEmbed(url)
    );

    // Trata oembed sem atributos específicos (pega a URL do conteúdo)
    processedHtml = processedHtml.replace(
        /<oembed>([^<]*)<\/oembed>/gi,
        (match, url) => createVideoEmbed(url)
    );

    // Trata figure.media com oembed dentro
    processedHtml = processedHtml.replace(
        /<figure class="media">[\s\S]*?<oembed[^>]*url="([^"]*)"[^>]*>[\s\S]*?<\/figure>/gi,
        (match, url) => `<figure class="media">${createVideoEmbed(url)}</figure>`
    );

    return processedHtml;
}

function createVideoEmbed(url) {
    if (!url) return '';

    // YouTube
    const youtubeMatch = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    if (youtubeMatch) {
        return `<div class="video-wrapper"><iframe src="https://www.youtube.com/embed/${youtubeMatch[1]}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`;
    }

    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
        return `<div class="video-wrapper"><iframe src="https://player.vimeo.com/video/${vimeoMatch[1]}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`;
    }

    // Dailymotion
    const dailymotionMatch = url.match(/dailymotion\.com\/video\/([a-zA-Z0-9]+)/);
    if (dailymotionMatch) {
        return `<div class="video-wrapper"><iframe src="https://www.dailymotion.com/embed/video/${dailymotionMatch[1]}" frameborder="0" allowfullscreen></iframe></div>`;
    }

    // Spotify
    const spotifyMatch = url.match(/spotify\.com\/(track|album|playlist|episode)\/([a-zA-Z0-9]+)/);
    if (spotifyMatch) {
        return `<div class="video-wrapper" style="padding-bottom: 152px;"><iframe src="https://open.spotify.com/embed/${spotifyMatch[1]}/${spotifyMatch[2]}" frameborder="0" allowtransparency="true" allow="encrypted-media"></iframe></div>`;
    }

    // Fallback - retorna link
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
}

// Estilos oficiais do CKEditor 5 para renderização de conteúdo
const StyledContent = styled.div`
    /* Base styles */
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 16px;
    line-height: 1.6;
    color: #333;
    word-wrap: break-word;

    /* Paragraphs */
    p {
        margin: 0 0 1em;
    }

    /* Headings */
    h1 {
        font-size: 2.3em;
        font-weight: 700;
        margin: 0.67em 0;
    }

    h2 {
        font-size: 1.84em;
        font-weight: 700;
        margin: 0.73em 0;
    }

    h3 {
        font-size: 1.48em;
        font-weight: 700;
        margin: 0.78em 0;
    }

    h4 {
        font-size: 1.22em;
        font-weight: 700;
        margin: 0.83em 0;
    }

    h5 {
        font-size: 1em;
        font-weight: 700;
        margin: 0.88em 0;
    }

    h6 {
        font-size: 0.85em;
        font-weight: 700;
        margin: 0.93em 0;
    }

    /* Lists */
    ul, ol {
        margin: 1em 0;
        padding-left: 40px;
    }

    ul {
        list-style-type: disc;
    }

    ul ul {
        list-style-type: circle;
    }

    ul ul ul {
        list-style-type: square;
    }

    ol {
        list-style-type: decimal;
    }

    ol ol {
        list-style-type: lower-latin;
    }

    ol ol ol {
        list-style-type: lower-roman;
    }

    li {
        margin: 0.5em 0;
    }

    /* Images */
    img {
        max-width: 100%;
        height: auto;
    }

    figure {
        margin: 1em 0;
        display: table;
    }

    figure.image {
        display: block;
        margin: 1em auto;
        text-align: center;
    }

    figure.image img {
        display: block;
        margin: 0 auto;
        max-width: 100%;
        height: auto;
    }

    figure.image.image-style-side {
        float: right;
        margin-left: 1.5em;
        max-width: 50%;
    }

    figure.image.image-style-align-left {
        float: left;
        margin-right: 1.5em;
    }

    figure.image.image-style-align-center {
        margin-left: auto;
        margin-right: auto;
    }

    figure.image.image-style-align-right {
        float: right;
        margin-left: 1.5em;
    }

    figcaption {
        background-color: #f7f7f7;
        caption-side: bottom;
        color: #333;
        display: table-caption;
        font-size: 0.75em;
        outline-offset: -1px;
        padding: 0.6em;
        text-align: center;
        word-break: break-word;
    }

    /* Blockquote */
    blockquote {
        border-left: 5px solid #ccc;
        font-style: italic;
        margin-left: 0;
        margin-right: 0;
        overflow: hidden;
        padding-left: 1.5em;
        padding-right: 1.5em;
    }

    /* Links */
    a {
        color: #1890ff;
        text-decoration: none;
    }

    a:hover {
        text-decoration: underline;
    }

    /* Tables */
    table {
        border-collapse: collapse;
        border-spacing: 0;
        width: 100%;
        margin: 1em 0;
    }

    table td,
    table th {
        border: 1px solid #bfbfbf;
        min-width: 2em;
        padding: 0.4em;
    }

    table th {
        background: rgba(0, 0, 0, 0.05);
        font-weight: 700;
    }

    /* Code */
    code {
        background-color: hsla(0, 0%, 78%, 0.3);
        border-radius: 2px;
        padding: 0.15em 0.3em;
        font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
        font-size: 0.9em;
    }

    pre {
        background: hsla(0, 0%, 78%, 0.3);
        border: 1px solid #c4c4c4;
        border-radius: 2px;
        direction: ltr;
        font-style: normal;
        min-width: 200px;
        overflow-x: auto;
        padding: 1em;
        tab-size: 4;
        text-align: left;
        white-space: pre-wrap;
    }

    pre code {
        background: transparent;
        border-radius: 0;
        padding: 0;
    }

    /* Horizontal line */
    hr {
        background: #dedede;
        border: 0;
        height: 4px;
        margin: 15px 0;
    }

    /* Text styles */
    strong, b {
        font-weight: 700;
    }

    em, i {
        font-style: italic;
    }

    u {
        text-decoration: underline;
    }

    s, strike {
        text-decoration: line-through;
    }

    sub {
        vertical-align: sub;
        font-size: smaller;
    }

    sup {
        vertical-align: super;
        font-size: smaller;
    }

    /* Font size classes */
    .text-tiny {
        font-size: 0.7em;
    }

    .text-small {
        font-size: 0.85em;
    }

    .text-big {
        font-size: 1.4em;
    }

    .text-huge {
        font-size: 1.8em;
    }

    /* Media embed / Video */
    figure.media {
        clear: both;
        margin: 1em 0;
        display: block;
    }

    .video-wrapper {
        position: relative;
        padding-bottom: 56.25%; /* 16:9 aspect ratio */
        height: 0;
        overflow: hidden;
        max-width: 100%;
        background: #000;
        border-radius: 8px;
    }

    .video-wrapper iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
    }

    /* Fallback para oembed não processado */
    oembed {
        display: block;
        padding: 1em;
        background: #f5f5f5;
        border-radius: 4px;
        text-align: center;
    }

    oembed::before {
        content: '🎬 Vídeo: ';
    }

    /* Mention */
    .mention {
        background: #e8f4fc;
        color: #1890ff;
    }

    /* To-do list */
    .todo-list {
        list-style: none;
        padding-left: 0;
    }

    .todo-list li {
        margin-bottom: 0.5em;
    }

    .todo-list .todo-list__label > input {
        margin-right: 0.5em;
    }

    /* Marker (highlight) */
    mark, .marker-yellow {
        background-color: #fdfd77;
    }

    .marker-green {
        background-color: #62f962;
    }

    .marker-pink {
        background-color: #fc7899;
    }

    .marker-blue {
        background-color: #72ccfd;
    }

    .pen-red {
        background-color: transparent;
        color: #e71313;
    }

    .pen-green {
        background-color: transparent;
        color: #128a00;
    }

    /* Clear floats */
    &::after {
        content: '';
        display: block;
        clear: both;
    }
`;

export default function CKEditorContent({ content, className, style }) {
    const processedContent = useMemo(() => {
        return convertOembedToIframe(content);
    }, [content]);

    if (!content) return null;

    return (
        <StyledContent
            className={className}
            style={style}
            dangerouslySetInnerHTML={{ __html: processedContent }}
        />
    );
}